import { useState, useEffect, useRef } from "react";
import { toSlug } from "utils";
import styles from "./Security_Vendor_Data_Services.module.css";
import business_people from "../assets/images/businesspeople_discussion.jpg";
import onboardingImage from "../assets/images/onboading-video-placeholder.jpg";
import youtube_video from "../assets/images/youtube.svg";
import tick_mark from "../assets/images/check-mark.svg";
import StickyHeader from "./StickyHeader";
import { Link, useParams, Navigate } from "react-router-dom";

const SecurityVendorDataServices = ({ data }) => {
  const { slug } = useParams();
  // items = useMemo(() => items.map(mapIcons), []);
  const activeTab =
    data.find((tab) => `${toSlug(tab.name)}` === slug.toLowerCase()) || data[0];
  const [expanded, setExpanded] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(0);
  const videoRef = useRef(null); // Track which dropdown is open

  useEffect(() => {
    setOpenDropdown(activeTab);
    // Scroll to the section after component mounts/updates
    if (activeTab) {
      const HEADER_OFFSET = 90;
      const sectionId = `section-${toSlug(activeTab.name)}`;
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const elementTop = el.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementTop - HEADER_OFFSET;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [activeTab]);

  const handleStartVideo = () => {
    setExpanded(true); // expand after attempting play
  };

  // when collapsing, pause and reset
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (expanded) {
      v.muted = true; // required for reliable autoplay
      v.playsInline = true; // iOS
      // wait a frame so CSS visibility applies
      requestAnimationFrame(() => {
        v.play().catch(() => {});
      });
    } else {
      try {
        v.pause();
      } catch {}
      v.currentTime = 0;
    }
  }, [expanded]);

  const handleDropdownToggle = (index) => {
    setOpenDropdown(openDropdown === index ? -1 : index);
  };

  const renderContent = (item) => (
    <article
      className={styles.contentWrapper}
      id={`section-${toSlug(item.name)}`}
    >
      <section className={styles.sectionOneWrapper}>
        <header className={styles.headerContainer}>
          <h1 className={styles.header}>{item.data.title}</h1>
        </header>

        <div className={styles.textContainer}>
          <h2 className={styles.sectionOneHeader}>{item.data.subTitle}</h2>
          <p className={styles.sectionOneDescription}>
            {item.data.description}
          </p>
          <p className={styles.sectionOneSubText}>{item.data.subText}</p>

          <div className={styles.sectionOneStepsContainer}>
            <ol className={styles.sectionOneStepWrapper}>
              {item.data.steps.map((step) => (
                <li key={step} className={styles.sectionOneStepListWrapper}>
                  <h3 className={styles.sectionOneStep}>{step}</h3>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className={styles.sectionTwoWrapper}>
        <div className={styles.sectionTwoTextContainer}>
          <h2 className={styles.sectionTwoHeader}>
            Why iCredentialing is the Right Choice for You
          </h2>
          <p className={styles.sectionTwoDescription}>
            Our unique methodology ensures accuracy, efficiency, and compliance,
            reducing administrative burdens while improving timeliness and
            reimbursement cycles.
          </p>
        </div>

        <div className={styles.sectionTwoVideoWrapper}>
          <figure className={styles.sectionTwoVideoContainer}>
            <img
              src={onboardingImage}
              className={`${styles.sectionTwoVideo} ${
                expanded ? styles.hidden : ""
              }`}
              alt="Onboarding preview"
            />
            <figcaption className={styles.sectionTwoVideoOverlay}>
              <button
                className={styles.sectionTwoVideoOverlay}
                onClick={handleStartVideo}
              >
                <img
                  src={youtube_video}
                  className={styles.sectionTwoVideoPlayButtonImage}
                  alt="Play video"
                />
                <span className={styles.sectionTwoVideoOverlayText}>
                  Watch 2:00 min video
                </span>
              </button>
            </figcaption>

            <video
              ref={videoRef}
              className={`${styles.sectionTwoVideo} ${styles.videoPlayable} ${
                expanded ? "" : styles.collapsed
              }`}
              controls
              autoPlay
              muted
              playsInline
              preload="auto"
              onCanPlay={() => {
                const v = videoRef.current;
                if (expanded && v) v.play().catch(() => {});
              }}
            >
              <source
                src="https://ihs-frontend-videos-and-assets.s3.us-east-2.amazonaws.com/onboarding.mp4"
                type="video/mp4"
              />
            </video>

            {expanded && (
              <button
                className={styles.sectionTwoVideoCloseButton}
                onClick={() => setExpanded(false)}
                aria-label="Close video"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-x"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </figure>

          <div className={styles.sectionTwoCardContainer}>
            {item.data.cardInfo.map((info) => (
              <div key={info} className={styles.sectionTwoCardWrapper}>
                <img
                  src={tick_mark}
                  className={styles.sectionTwoVideoPlayButtonImage}
                  alt="Checkmark"
                />
                <span className={styles.sectionTwoCardText}>{info}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </article>
  );

  if (!activeTab) {
    return <Navigate to={`/services/${toSlug(data[0].name)}`} replace />;
  }

  return (
    <main className={styles.container}>
      <title>{activeTab.title}</title>
      <meta name="description" content={activeTab.description} />

      <section className={styles["services-headcontainer"]}>
        <header className={styles["services-headcontent"]}>
          <h2>Connecting healthcare systems for better outcomes</h2>
          <h3>
            At iCredentialing, we leverage AWS-based automation and machine
            learning to ensure seamless interoperability and secure data
            exchange across healthcare departments, ecosystems, and vendors -
            while supporting HIPAA compliance through comprehensive Security
            Risk Assessments (SRA) and ensuring patient protection.
          </h3>
        </header>
        <figure className={styles.imageHolder}>
          <img
            src={business_people}
            alt="A diverse group of four smiling professionals in an office setting gathered around a laptop, conveying teamwork and cooperation. Bright, airy atmosphere."
            className={styles.businessImage}
          />
          <figcaption className={styles.Credit}>Designed by FreePik</figcaption>
        </figure>
      </section>

      {/* Desktop Navigation */}
      <StickyHeader childClass={styles.subheaderContentWrapper}>
        <nav className={styles.subheaderContentWrapper}>
          <ul className={styles.servicesNavMenuContainer}>
            {data.map((ele) => {
              return (
                <li key={toSlug(ele.name)}>
                  <Link
                    className={`${styles.servicesNavMenuItems} ${
                      ele.name === activeTab.name && styles.selected
                    }`}
                    to={`/services/${toSlug(ele.name)}`}
                  >
                    <img
                      src={
                        ele.name === activeTab.name ? ele.activeIcon : ele.icon
                      }
                      className={styles.subHeaderIcon}
                      alt={ele.name}
                    />
                    <span>{ele.name}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </StickyHeader>

      {/* Desktop Content */}
      <section className={styles.desktopContent}>
        {data.map((item) => renderContent(item))}
      </section>

      {/* Mobile/Tablet Accordion */}
      <section className={styles.mobileContent}>
        {data.map((item, index) => (
          <article
            key={`mobile-content-${toSlug(item.name)}`}
            className={styles.accordionItem}
          >
            <header
              className={`${styles.accordionHeader} ${
                openDropdown === index ? styles.accordionHeaderActive : ""
              }`}
              onClick={() => handleDropdownToggle(index)}
            >
              <div className={styles.accordionHeaderContent}>
                <img
                  src={openDropdown === index ? item.activeIcon : item.icon}
                  className={styles.accordionIcon}
                  alt={item.name}
                />
                <span className={styles.accordionTitle}>{item.name}</span>
              </div>
              <div
                className={`${styles.accordionArrow} ${
                  openDropdown === index ? styles.accordionArrowRotated : ""
                }`}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </header>

            <section
              className={`${styles.accordionContent} ${
                openDropdown === index ? styles.accordionContentOpen : ""
              }`}
            >
              {renderContent(item)}
            </section>
          </article>
        ))}
      </section>
    </main>
  );
};

export default SecurityVendorDataServices;
